<template>
  <!--
    Doorman entry simulator — shows the menu a seeker lands on (avatar + the
    mode buttons) and, on tap, streams that mode's intro copy. Pure preview:
    the intro is the operator's authored "streamed when tapped" text, so it
    reflects unsaved edits instantly with no backend call.
  -->
  <div class="doorman-preview">
    <button type="button" class="dp-close" @click="$emit('close')">Close <span aria-hidden="true">✕</span></button>
    <header class="dp-head">
      <img v-if="avatarUrl" :src="avatarUrl" class="dp-avatar" :alt="readerName" />
      <div v-else class="dp-avatar placeholder">✦</div>
      <span class="dp-name">{{ readerName }}</span>
    </header>

    <div class="dp-body">
      <template v-if="!active">
        <p v-if="greeting" class="dp-greeting">{{ greeting }}</p>
        <button
          v-for="m in modes" :key="m.key"
          type="button" class="dp-btn"
          @click="tap(m)"
        >{{ m.label }}</button>
        <p v-if="!modes.length" class="dp-empty">No Doorman buttons yet.</p>
      </template>
      <template v-else>
        <div class="dp-chosen">{{ active.label }}</div>
        <!-- Action modes (skill buttons) don't speak — they take an action
             (deduct emeralds, start the reading). Show that, don't stream. -->
        <p v-if="active.action" class="dp-resume">{{ active.action }}</p>
        <p v-else class="dp-intro">{{ shown }}<span v-if="streaming" class="dp-caret">▍</span></p>
        <button type="button" class="dp-back" @click="reset">↩ Back to menu</button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'

defineProps({
  // [{ key, label, intro, action }] — when `action` is set, tapping shows that
  // note instead of streaming an intro (e.g. a skill button deducts emeralds
  // and starts the reading rather than speaking).
  modes: { type: Array, default: () => [] },
  // The entry greeting shown above the buttons.
  greeting: { type: String, default: '' },
  avatarUrl: { type: String, default: '' },
  readerName: { type: String, default: 'Your reader' },
})
defineEmits(['close'])

const active = ref(null)
const shown = ref('')
const streaming = ref(false)
let timer = null

function stop() {
  if (timer) { clearInterval(timer); timer = null }
  streaming.value = false
}

// Typewriter the authored intro so the preview reads like the live stream.
function stream(text) {
  stop()
  shown.value = ''
  if (!text) return
  streaming.value = true
  let i = 0
  const step = Math.max(1, Math.round(text.length / 80))
  timer = setInterval(() => {
    i += step
    shown.value = text.slice(0, i)
    if (i >= text.length) { shown.value = text; stop() }
  }, 22)
}

function tap(mode) {
  active.value = mode
  // Action modes (skill buttons) take an action instead of speaking — no stream.
  if (mode.action) { stop(); shown.value = ''; return }
  stream(mode.intro || '(No intro text yet - add some above.)')
}
function reset() {
  stop()
  active.value = null
  shown.value = ''
}

onBeforeUnmount(stop)
</script>

<style scoped>
.doorman-preview {
  position: relative;
  background: linear-gradient(160deg, #160d2e, #0d0520);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 14px;
  padding: 18px;
}
.dp-close {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.78rem;
  line-height: 1;
  cursor: pointer;
}
.dp-close:hover { color: #fff; border-color: rgba(255, 255, 255, 0.32); }
.dp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 14px;
}
.dp-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  object-fit: cover; border: 1px solid rgba(255, 255, 255, 0.15);
}
.dp-avatar.placeholder {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.06); color: #c9b8ff;
}
.dp-name { font-weight: 600; color: #E8D8FF; }
.dp-tag { font-size: 12px; font-style: italic; color: rgba(195, 169, 255, 0.5); }
.dp-body { display: flex; flex-direction: column; gap: 10px; }
.dp-greeting { margin: 0 0 4px; font-size: 14px; line-height: 1.55; color: #E8D8FF; }
.dp-btn {
  width: 100%;
  text-align: left;
  background: rgba(195, 169, 255, 0.08);
  border: 1px solid rgba(195, 169, 255, 0.3);
  border-radius: 999px;
  color: #E8D8FF;
  padding: 11px 18px;
  font-size: 14px;
  font-family: 'Cinzel', serif;
  cursor: pointer;
  transition: all 0.15s;
}
.dp-btn:hover { background: rgba(201, 169, 78, 0.16); border-color: #C9A94E; color: #fff; }
.dp-empty { margin: 0; font-size: 12px; font-style: italic; opacity: 0.5; }
.dp-chosen {
  align-self: flex-start;
  background: rgba(201, 169, 78, 0.16);
  border: 1px solid rgba(201, 169, 78, 0.45);
  color: #C9A94E;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13px;
  font-family: 'Cinzel', serif;
}
.dp-intro {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #E8D8FF;
  white-space: pre-wrap;
}
.dp-resume {
  margin: 0;
  font-size: 13px;
  font-style: italic;
  line-height: 1.6;
  color: rgba(195, 169, 255, 0.7);
}
.dp-caret { opacity: 0.6; animation: dpblink 1s steps(2) infinite; }
@keyframes dpblink { 50% { opacity: 0; } }
.dp-back {
  align-self: flex-start;
  margin-top: 4px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 12px;
  cursor: pointer;
}
.dp-back:hover { color: #fff; border-color: rgba(255, 255, 255, 0.32); }
</style>
